/**
 * Subcontractor detail screen.
 *
 * Shows the subcontractor's fields (GET /api/subcontractors/:id) and an inline
 * "Add login" form that provisions a portal user (POST /api/subcontractors/:id/users).
 *
 * NEVER imports @smartbox/* (mobile CLAUDE.md invariant 1). No raw style values.
 */
import { useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { useLocalSearchParams } from 'expo-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getSubcontractor,
  provisionSubUser,
  type ProvisionSubUserRequest,
} from '../../../src/api/subcontractors';
import { Screen, Text, Badge, Card, Button, Field } from '../../../src/components/ui';
import { theme } from '../../../src/theme';
import { extractErrorMessage } from '../../../src/lib/format';

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.fieldRow}>
      <Text variant="small" color="secondary">
        {label}
      </Text>
      <Text variant="body">{value}</Text>
    </View>
  );
}

export default function SubcontractorDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const subcontractorId = String(id);
  const queryClient = useQueryClient();

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [provisioned, setProvisioned] = useState(false);

  const subQuery = useQuery({
    queryKey: ['subcontractor', subcontractorId],
    queryFn: () => getSubcontractor(subcontractorId),
    staleTime: 30_000,
  });

  const provisionMutation = useMutation({
    mutationFn: (body: ProvisionSubUserRequest) => provisionSubUser(subcontractorId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['subcontractor', subcontractorId] });
      setEmail('');
      setFullName('');
      setPhone('');
      setFormError(null);
      setProvisioned(true);
    },
    onError: (err) => setFormError(extractErrorMessage(err, 'Could not add login.')),
  });

  const onProvision = () => {
    setFormError(null);
    setProvisioned(false);
    if (!email.trim()) {
      setFormError('Enter an email address.');
      return;
    }
    if (!fullName.trim()) {
      setFormError('Enter a full name.');
      return;
    }
    const body: ProvisionSubUserRequest = {
      email: email.trim(),
      full_name: fullName.trim(),
    };
    if (phone.trim()) body.phone = phone.trim();
    provisionMutation.mutate(body);
  };

  if (subQuery.isLoading) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <ActivityIndicator
            size="large"
            color={theme.color.action.primary}
            accessibilityLabel="Loading subcontractor"
          />
          <Text variant="body" color="secondary">
            Loading subcontractor…
          </Text>
        </View>
      </Screen>
    );
  }

  if (subQuery.isError || !subQuery.data) {
    return (
      <Screen>
        <View style={styles.centeredState}>
          <Card>
            <Text variant="bodyStrong" color="danger" accessibilityRole="alert">
              {extractErrorMessage(subQuery.error, 'Failed to load subcontractor.')}
            </Text>
            <Button
              title="Retry"
              variant="secondary"
              size="sm"
              fullWidth={false}
              onPress={() => subQuery.refetch()}
              accessibilityLabel="Retry loading subcontractor"
            />
          </Card>
        </View>
      </Screen>
    );
  }

  const sub = subQuery.data;

  return (
    <Screen scroll>
      {/* Header */}
      <View style={styles.header}>
        <Text variant="title">{sub.name}</Text>
        <View style={styles.headerMeta}>
          {sub.is_1099 ? <Badge status="sent" label="1099" /> : null}
          <Badge status={sub.is_active ? 'paid' : 'draft'} label={sub.is_active ? 'Active' : 'Inactive'} />
        </View>
      </View>

      {/* Fields */}
      <Card>
        <FieldRow label="License number" value={sub.license_number ?? '—'} />
        <FieldRow
          label="Insurance expires"
          value={sub.insurance_expires_on ? new Date(sub.insurance_expires_on).toLocaleDateString() : '—'}
        />
        <FieldRow label="W-9 on file" value={sub.w9_on_file ? 'Yes' : 'No'} />
        <FieldRow label="1099 contractor" value={sub.is_1099 ? 'Yes' : 'No'} />
      </Card>

      {/* Add login form */}
      <Card>
        <Text variant="bodyStrong">Add login</Text>
        <Text variant="small" color="secondary">
          Provision a portal login so this subcontractor can sign in.
        </Text>
        <Field
          label="Email"
          placeholder="name@company.com"
          value={email}
          onChangeText={setEmail}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          accessibilityLabel="Login email"
        />
        <Field
          label="Full name"
          placeholder="e.g. Jordan Smith"
          value={fullName}
          onChangeText={setFullName}
          autoCapitalize="words"
          accessibilityLabel="Login full name"
        />
        <Field
          label="Phone (optional)"
          placeholder="e.g. (555) 123-4567"
          value={phone}
          onChangeText={setPhone}
          keyboardType="phone-pad"
          accessibilityLabel="Login phone"
        />
        {formError ? (
          <Text variant="small" color="danger" accessibilityRole="alert">
            {formError}
          </Text>
        ) : null}
        {provisioned ? (
          <Text variant="small" color="link">
            Login provisioned.
          </Text>
        ) : null}
        <Button
          title="Add login"
          onPress={onProvision}
          loading={provisionMutation.isPending}
          disabled={provisionMutation.isPending || !email.trim() || !fullName.trim()}
          accessibilityLabel="Add subcontractor login"
        />
      </Card>
    </Screen>
  );
}

const styles = StyleSheet.create({
  centeredState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing.md,
    paddingHorizontal: theme.spacing['2xl'],
  },
  header: { gap: theme.spacing.sm },
  headerMeta: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.sm },
  fieldRow: { gap: 2 },
});
